import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Autocomplete,
  LinearProgress,
  Divider,
  Card,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InventoryIcon from "@mui/icons-material/Inventory";
import configServ from "../../services/config";
import ItemsSelection from "./ItemsSelection";

const itemListInitial = [
  { id: Date.now(), itemname: "", qty: "", rate: "", amount: 0 },
];

const CustomerSiteTransfer = () => {
  const { company_id, user_id } = useSelector((state) => state.GlobalVariables);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [challans, setChallans] = useState([]);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [items, setItems] = useState([]); // Master Items for Autocomplete
  const [challanItems, setChallanItems] = useState([]); // Items fetched from Challan
  const [transferCustomer, setTransferCustomer] = useState(null);
  const [shipToAddresses, setShipToAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(false);

  // Manual Item Selection States
  const [itemList, setItemList] = useState(itemListInitial);
  const [itemListError, setItemListError] = useState({});

  const isChallanSelected = !!selectedChallan;

  // ================= FETCHING FUNCTIONS =================
  const fetchCustomerList = async () => {
    try {
      const result = await configServ.get_customer({ company_id });
      setCustomers(result || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMasterItems = async () => {
    try {
      const res = await configServ.getItemList({ company_id }); // Adjust API call as per your service
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchChallan = async (customer_id) => {
    setLoading(true);
    try {
      const res = await configServ.getAllChallan({ company_id, customer_id });
      if (res.success) setChallans(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchChallanItems = async (challan_id) => {
    setLoading(true);
    try {
      const res = await configServ.getItemsThroughChallanId({ challan_id });
      if (res.success) {
        setChallanItems(
          res.data.map((item) => ({ ...item, transfer_qty: "" })),
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchShipToAddress = async (cust_id) => {
    try {
      const result = await configServ.getShipToAddress({ cust_id });
      setShipToAddresses(result || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchCustomerList();
    fetchMasterItems();
  }, []);

  // ================= ITEM SELECTION HANDLERS =================
  const handleAutocompleteChange = (newValue, id) => {
    const value = newValue || "";
    handleInputChange({ target: { name: "itemname", value } }, id);
  };

  const handleInputChange = (event, id) => {
    const { name, value } = event.target;
    const updatedList = itemList.map((item) => {
      if (item.id === id) {
        const updatedItem = { ...item, [name]: value };

        if (name === "qty" || name === "rate") {
          const q = parseFloat(updatedItem.qty) || 0;
          const r = parseFloat(updatedItem.rate) || 0;
          updatedItem.amount = (q * r).toFixed(2);
        }

        if (name === "itemname") {
          const selected = items.find(
            (i) => i.item_name?.toLowerCase() === value.toLowerCase(),
          );
          if (selected) {
            return {
              ...updatedItem,
              itemid: selected.id,
              item_code: selected.item_code,
              unit: selected.unit,
              rate: selected.billable_rate || 0,
              qty: "",
              amount: 0,
            };
          }
        }
        return updatedItem;
      }
      return item;
    });
    setItemList(updatedList);
  };

  const handleAddItem = (currentItemId) => {
    const currentIndex = itemList.findIndex(
      (item) => item.id === currentItemId,
    );
    const newItem = {
      id: Date.now(),
      itemname: "",
      qty: "",
      rate: "",
      amount: 0,
    };
    const newList = [...itemList];
    newList.splice(currentIndex + 1, 0, newItem);
    setItemList(newList);
  };

  const handleDeleteItem = (data) => {
    if (itemList.length > 1) {
      setItemList(itemList.filter((item) => item.id !== data.id));
    } else {
      setItemList(itemListInitial);
    }
  };

  const handleChallanQtyChange = (value, index) => {
    const updated = [...challanItems];
    if (value === "") {
      updated[index].transfer_qty = "";
      setChallanItems(updated);
      return;
    }
    const val = parseFloat(value);
    if (val > parseFloat(updated[index].qty)) {
      Swal.fire(
        "Limit Exceeded",
        `Max available is ${updated[index].qty}`,
        "warning",
      );
      return;
    }
    updated[index].transfer_qty = value;
    setChallanItems(updated);
  };

  // ================= SUBMIT LOGIC =================
  const handleTransfer = async () => {
    if (!selectedCustomer || !transferCustomer || !selectedAddress) {
      Swal.fire(
        "Required",
        "Please complete customer and address selections",
        "info",
      );
      return;
    }

    let finalItems = [];
    if (isChallanSelected) {
      finalItems = challanItems
        .filter((i) => i.transfer_qty !== "" && parseFloat(i.transfer_qty) > 0)
        .map((i) => ({ ...i, transfer_qty: parseFloat(i.transfer_qty) }));
    } else {
      finalItems = itemList
        .filter((i) => i.itemid && parseFloat(i.qty) > 0)
        .map((i) => ({ ...i, transfer_qty: parseFloat(i.qty) }));
    }

    if (finalItems.length === 0) {
      Swal.fire(
        "No Items",
        "Please add at least one valid item with quantity",
        "warning",
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        company_id,
        from_customer_id: selectedCustomer.id,
        to_customer_id: transferCustomer.id,
        challan_id: selectedChallan?.id || null, // null if manual entry
        ship_to_id: selectedAddress.id,
        items: finalItems,
        is_manual_entry: !isChallanSelected, // Flag for backend
        user_id: user_id,
      };

      // console.log("transferCustomerItems", payload);

      const rest = await configServ.transferCustomerItems(payload);
      if (rest.success) {
        Swal.fire("Success", "Transfer completed successfully", "success");

        // Reset
        setChallanItems([]);
        setItemList(itemListInitial);
        setSelectedChallan(null);
        setSelectedCustomer(null);
        setTransferCustomer(null);
        setSourceAddress("");
        setSelectedAddress(null);
        setShipToAddresses([]);
        fetchCustomerList(); // Refresh customer list in case of any changes
      }
    } catch (e) {
      Swal.fire("Error", "Transfer failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const [sourceAddress, setSourceAddress] = useState("");

  return (
    <Box
      sx={{
        p: { xs: 1, md: 4 },
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          border: "1px solid #e0e0e0",
        }}
      >
        <Box display="flex" alignItems="center" mb={3}>
          <SwapHorizIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
          <Typography variant="h5" fontWeight="700">
            Site Transfer
          </Typography>
        </Box>

        {loading && (
          <LinearProgress sx={{ mb: 2, height: 4, borderRadius: 2 }} />
        )}

        <Grid container spacing={3}>
          {/* Source Section */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="overline"
              color="textSecondary"
              fontWeight="bold"
            >
              Source Details
            </Typography>
            <Autocomplete
              options={customers}
              getOptionLabel={(o) =>
                o ? `${o.fname} (${o.address?.substring(0, 40)}...)` : ""
              }
              value={selectedCustomer}
              onChange={(_, val) => {
                setSelectedCustomer(val);
                setSelectedChallan(null);

                if (val) {
                  fetchChallan(val.id);
                  setSourceAddress(val.address || "");
                } else {
                  setSourceAddress("");
                }
              }}
              renderInput={(p) => (
                <TextField {...p} label="From Customer" margin="dense" />
              )}
            />
            <TextField
              label="Source Address"
              value={sourceAddress}
              fullWidth
              margin="dense"
              multiline
              minRows={2}
              disabled
            />
            <Autocomplete
              options={challans}
              getOptionLabel={(o) => o.challan_no || ""}
              value={selectedChallan}
              onChange={(_, val) => {
                setSelectedChallan(val);
                if (val) fetchChallanItems(val.id);
                else setChallanItems([]); // Reset if cleared
              }}
              disabled={!selectedCustomer}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Select Challan (Optional)"
                  placeholder="Leave empty for manual entry"
                  margin="dense"
                />
              )}
            />
          </Grid>

          {/* Destination Section */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="overline"
              color="textSecondary"
              fontWeight="bold"
            >
              Destination Details
            </Typography>
            <Autocomplete
              options={customers.filter((c) => c.id !== selectedCustomer?.id)}
              getOptionLabel={(o) => o.fname || ""}
              value={transferCustomer}
              onChange={(_, val) => {
                setTransferCustomer(val);
                if (val) fetchShipToAddress(val.id);
              }}
              renderInput={(p) => (
                <TextField {...p} label="To Customer" margin="dense" />
              )}
            />
            <Autocomplete
              options={shipToAddresses}
              getOptionLabel={(o) => o.address || ""}
              value={selectedAddress}
              onChange={(_, val) => setSelectedAddress(val)}
              disabled={!transferCustomer}
              renderInput={(p) => (
                <TextField {...p} label="Ship To Address" margin="dense" />
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Dynamic Items Section */}
        {isChallanSelected ? (
          // View 1: Items from Challan
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <InventoryIcon fontSize="small" /> Items from Challan
            </Typography>
            {/* ... Your Existing Challan Table/Card Logic ... */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Available(Qty)</TableCell>
                    <TableCell align="right">Price</TableCell>

                    <TableCell align="center" width="150px">
                      Transfer Qty
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {challanItems.map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.itemname}</TableCell>
                      <TableCell align="right">{row.qty}</TableCell>
                      <TableCell align="right">{row.rate}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={row.transfer_qty}
                          onChange={(e) =>
                            handleChallanQtyChange(e.target.value, index)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          // View 2: Manual Item Selection
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Manual Item Selection
            </Typography>
            <ItemsSelection
              itemList={itemList}
              handleAutocompleteChange={handleAutocompleteChange}
              handleInputChange={handleInputChange}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              items={items} // Passing Master Items
              errors={itemListError}
            />
          </Box>
        )}

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleTransfer}
            disabled={loading}
            sx={{ borderRadius: 2, px: 6, textTransform: "none" }}
          >
            {loading ? "Processing..." : "Complete Transfer"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomerSiteTransfer;
